import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useGetPaymentLocationFormDetails } from "api/queries/location/useGetPaymentLocationFormDetails";
import { useGetShipmentInvoices } from "api/queries/invoice/useGetShipmentInvoices";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import { updateShipmentDetails } from "api/services/shipment";
import { getPaymentLocationDetailsParams } from "@RequestFormV2/utils/getPaymentLocationDetailsParams";
import OrganisationDynamicField from "@RequestFormV2/sections/Organisation/components/ShipmentDetailsSection/OrganisationDynamicField";
import { mapDropdownOptions } from "@RequestFormV2/sections/Organisation/hooks/organisationFieldOptions";
import Button from "components/v2/Button/Button";
import Input from "components/v2/Input/Input";
import Modal from "components/v2/Modal/Modal";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import { IFieldProps } from "types/Organisational";

import {
  buildOrganisationFields,
  getCostRecoveryYesValue,
  getOrganisationDropdownOptions,
  isGridOrganisationField,
} from "./buildOrganisationFields";
import { buildOrganisationPrefill } from "./buildOrganisationPrefill";
import { buildShipmentUpdatePayload } from "./buildShipmentUpdatePayload";
import { isPrimaryInvoiceApproved } from "./isPrimaryInvoiceApproved";
import "./EditOrganisationModal.scss";

type EditOrganisationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  shipmentId: string;
  paymentLocation?: string;
};

type FormValues = {
  organizational: Record<string, string | null | undefined>;
  shipment: {
    title: string;
  };
};

const EMPTY_FORM: FormValues = {
  organizational: {},
  shipment: { title: "" },
};

const FORM_ID = "edit-organisation-after-delivery-form";

export default function EditOrganisationModal({
  isOpen,
  onClose,
  shipmentId,
  paymentLocation,
}: Readonly<EditOrganisationModalProps>) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const methods = useForm<FormValues>({ defaultValues: EMPTY_FORM });
  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    setValue,
  } = methods;
  const [disabledFields, setDisabledFields] = useState<Record<string, boolean>>({});
  const hasPrefilled = useRef(false);

  const reasonCodeGroup = useWatch({ control, name: "organizational.reason_code_group" });
  const reasonCode = useWatch({ control, name: "organizational.reason_code" });
  const costRecoveryRequired = useWatch({ control, name: "organizational.cost_recovery_required" });

  const locationDetailsParams = getPaymentLocationDetailsParams("quote", paymentLocation ?? "");
  const { data: locationDetails, isInitialLoading: isLocationDetailsLoading } = useGetPaymentLocationFormDetails({
    location: paymentLocation ?? "",
    isQuote: true,
    config: { params: locationDetailsParams.params },
    options: { enabled: isOpen && !!paymentLocation },
  });

  const { data: quoteDetails, isInitialLoading: isQuoteDetailsLoading } = useGetQuoteRequestDetails({
    shipmentId,
    options: { enabled: isOpen && !!shipmentId },
  });

  const { data: invoicesData } = useGetShipmentInvoices({
    shipmentId,
    options: { enabled: isOpen && !!shipmentId },
  });
  const isCostRecoveryLocked = isPrimaryInvoiceApproved(invoicesData?.results);

  const isLoading = isLocationDetailsLoading || isQuoteDetailsLoading;

  useEffect(() => {
    if (!isOpen) {
      hasPrefilled.current = false;
      setDisabledFields({});
      reset(EMPTY_FORM);
      return;
    }

    if (hasPrefilled.current || isLoading || !quoteDetails || !locationDetails) return;

    hasPrefilled.current = true;
    reset(buildOrganisationPrefill(quoteDetails?.organization, locationDetails?.organizational_options, paymentLocation));
  }, [isLoading, isOpen, locationDetails, paymentLocation, quoteDetails, reset]);

  const organisationFields = useMemo(
    () => buildOrganisationFields(locationDetails?.organizational_options, locationDetails?.order),
    [locationDetails],
  );

  const shipmentTitleDetails = locationDetails?.organizational_options.find((field) => field.field_id === "shipment_title");
  const costRecoveryField = organisationFields.find((field) => field.field_id === "cost_recovery_required");
  const costRecoverySubFields = organisationFields.filter((field) => field.field_id.startsWith("custom_cost_recovery_"));
  const gridFields = useMemo(
    () => organisationFields.filter((field) => isGridOrganisationField(field.field_id)),
    [organisationFields],
  );

  const yesValue = getCostRecoveryYesValue(organisationFields);
  const showCostRecovery = useMemo(() => {
    if (!organisationFields.length || !costRecoveryField) return false;
    if (costRecoveryField.mandatory) return true;
    const reasonCodeField = organisationFields.find((field) => field.field_id === "reason_code");
    if (reasonCode && reasonCodeField?.dropdown_values?.length) {
      const selected = (reasonCodeField.dropdown_values as Array<{ value: string; cost_recovery_possible?: boolean }>).find(
        (item) => item.value === reasonCode,
      );
      return Boolean(selected?.cost_recovery_possible);
    }
    return false;
  }, [organisationFields, costRecoveryField, reasonCode]);

  const costRecoveryOptions = useMemo(
    () =>
      ((costRecoveryField?.dropdown_values as Array<{ key: string; value: string }>) ?? [])
        .map((item) => ({ label: item.key, value: item.value }))
        .sort((a, b) => {
          if (a.value === yesValue) return -1;
          if (b.value === yesValue) return 1;
          return 0;
        }),
    [costRecoveryField, yesValue],
  );

  const { mutate: updateShipment, isLoading: isSaving } = useMutation({
    mutationFn: (values: FormValues) =>
      updateShipmentDetails(shipmentId, buildShipmentUpdatePayload(values, organisationFields, yesValue)),
    onSuccess: () => {
      toast.success(
        intl.formatMessage({
          id: "settings.notifications.mobile.shipmentIsUpdated",
          defaultMessage: "Shipment is updated",
        }),
      );
      queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(shipmentId) });
      queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(shipmentId) });
      queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteDetails(shipmentId) });
      queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteAuditTrailListing(shipmentId) });
      queryClient.invalidateQueries({ queryKey: [userQueryKeys.taskWidget] });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.userPendingTasks });
      onClose();
    },
    onError: (error) => errorToast(error),
  });

  const getDropdownOptions = (field: IFieldProps) =>
    getOrganisationDropdownOptions(field, organisationFields, reasonCodeGroup);

  const handleDropdownChange = (field: IFieldProps, value: string | null) => {
    if (field.field_id === "reason_code_group") setValue("organizational.reason_code", null);
    const { dropdown_values } = field;
    if (!dropdown_values) return;

    const previousValue = getValues(`organizational.${field.field_id}`);
    const previousOption = (
      dropdown_values as Array<{ value: string; updates?: Array<{ selector?: string; value?: string; disable?: boolean }> }>
    ).find((opt) => opt.value === previousValue);
    const newOption = (
      dropdown_values as Array<{ value: string; updates?: Array<{ selector?: string; value?: string; disable?: boolean }> }>
    ).find((opt) => opt.value === value);

    previousOption?.updates?.forEach((update) => {
      if (!update.selector?.startsWith("organizational.")) return;
      setValue(update.selector as `organizational.${string}`, null);
      if (update.disable) {
        setDisabledFields((current) => ({ ...current, [update.selector as string]: false }));
      }
    });
    newOption?.updates?.forEach((update) => {
      if (!update.selector?.startsWith("organizational.")) return;
      setValue(update.selector as `organizational.${string}`, update.value ?? null);
      if (update.disable) {
        setDisabledFields((current) => ({ ...current, [update.selector as string]: true }));
      }
    });
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="xl">
      <Modal.Header closeButtonTestId={testIds.SHIPMENT_DASHBOARD_EDIT_ORGANISATION_MODAL_CLOSE}>
        <div className="text-primary text-xl font-semibold">
          {intl.formatMessage({ id: "new-request.form.editShipment", defaultMessage: "Edit Shipment" })}
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="edit-organisation-modal" data-testid={testIds.SHIPMENT_DASHBOARD_EDIT_ORGANISATION_MODAL}>
          {isLoading ? (
            <div className="edit-organisation-modal__loading text-tertiary">
              {intl.formatMessage({ id: "general.loading", defaultMessage: "Loading..." })}
            </div>
          ) : (
            <FormProvider {...methods}>
              <form id={FORM_ID} onSubmit={handleSubmit((values) => updateShipment(values))}>
                <input type="hidden" {...methods.register("organizational.location")} />
                <input type="hidden" {...methods.register("organizational.invoice_location")} />

                <div className="edit-organisation-modal__fields">
                  {shipmentTitleDetails && (
                    <div className="edit-organisation-modal__full-width">
                      <Input
                        name="shipment.title"
                        label={
                          shipmentTitleDetails.label ||
                          intl.formatMessage({
                            id: "request-form.organisation.shipment-title",
                            defaultMessage: "Shipment title",
                          })
                        }
                        required={shipmentTitleDetails.mandatory}
                        testId={testIds.SHIPMENT_DASHBOARD_EDIT_ORGANISATION_TITLE}
                      />
                    </div>
                  )}

                  {gridFields.map((field) => (
                    <OrganisationDynamicField
                      key={field.field_id}
                      field={field}
                      getDropdownOptions={getDropdownOptions}
                      isDisabled={Boolean(disabledFields[`organizational.${field.field_id}`])}
                      unknownTypeBehavior="editable"
                      onDropdownChange={(value) => handleDropdownChange(field, value)}
                    />
                  ))}

                  {showCostRecovery && costRecoveryField && (
                    <OrganisationDynamicField
                      field={costRecoveryField}
                      getDropdownOptions={() => costRecoveryOptions}
                      isDisabled={isCostRecoveryLocked}
                      unknownTypeBehavior="editable"
                    />
                  )}

                  {showCostRecovery &&
                    costRecoveryRequired === yesValue &&
                    costRecoverySubFields.map((field) => (
                      <OrganisationDynamicField
                        key={field.field_id}
                        field={field}
                        getDropdownOptions={mapDropdownOptions}
                        isDisabled={Boolean(disabledFields[`organizational.${field.field_id}`])}
                        useInitialData
                        unknownTypeBehavior="editable"
                      />
                    ))}
                </div>
              </form>
            </FormProvider>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              variant="tertiary-color"
              label={intl.formatMessage({ id: "general.discard", defaultMessage: "Discard" })}
              onClick={onClose}
            />
          </div>
          {/* Saving stays disabled until the backend allows start-edit on delivered shipments (WEB-10988). */}
          <Button
            label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
            type="submit"
            form={FORM_ID}
            disabled={!isDirty || isSaving}
            testId={testIds.SHIPMENT_DASHBOARD_EDIT_ORGANISATION_SAVE}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}
